/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppNotification, AppNotificationKind, NotificationsService } from './notifications.service';

/**
 * The bell in the header.
 *
 * The badge counts rows that appeared since the menu was last opened —
 * closing the menu acknowledges them (they stay highlighted while it is open,
 * so the new ones can be told apart), and the marker survives reloads in
 * localStorage. Feeds refresh on open and once a minute while the app is up;
 * there is no push channel, so a new task can take up to a minute to ring.
 */
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatBadgeModule, MatMenuModule, MatTooltipModule, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      class="aca-shell-header__bell"
      mat-icon-button
      [matMenuTriggerFor]="menu"
      [matTooltip]="'DMS.NOTIF.TITLE' | translate"
      [attr.aria-label]="'DMS.NOTIF.TITLE' | translate"
      (menuOpened)="opened()"
      (menuClosed)="notifications.markAllSeen()"
    >
      <mat-icon
        [matBadge]="notifications.unread()"
        [matBadgeHidden]="notifications.unread() === 0"
        matBadgeColor="warn"
        matBadgeSize="small"
        matBadgeOverlap="false"
        >notifications_none</mat-icon
      >
    </button>

    <mat-menu #menu="matMenu" class="aca-notif-menu" xPosition="before" yPosition="below">
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <div class="aca-notif__head" (click)="$event.stopPropagation()">
        <span class="aca-notif__title">{{ 'DMS.NOTIF.TITLE' | translate }}</span>
        <button class="aca-notif__markall" mat-button *ngIf="notifications.unread() > 0" (click)="notifications.markAllSeen()">
          {{ 'DMS.NOTIF.MARK_ALL' | translate }}
        </button>
      </div>

      <ng-container *ngIf="notifications.items().length; else empty">
        <ng-container *ngFor="let group of groups">
          <ng-container *ngIf="byKind(group) as rows">
            <div class="aca-notif__group" *ngIf="rows.length">{{ 'DMS.NOTIF.KIND_' + group | translate }}</div>
            <button mat-menu-item class="aca-notif__item" *ngFor="let item of rows" (click)="open(item)">
              <mat-icon class="aca-notif__icon" [class.aca-notif__icon--late]="item.overdue">{{ iconFor(item.kind) }}</mat-icon>
              <span class="aca-notif__body">
                <span class="aca-notif__name">
                  {{ item.title }}
                  <span class="aca-notif__dot" *ngIf="!notifications.seenIds().has(item.id)"></span>
                </span>
                <span class="aca-notif__detail" [class.aca-notif__detail--late]="item.overdue">
                  {{ item.kind === 'expiry' ? (item.detail | acaDate: 'd MMM y') : item.detail }}
                </span>
              </span>
            </button>
          </ng-container>
        </ng-container>
      </ng-container>

      <ng-template #empty>
        <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
        <div class="aca-notif__empty" (click)="$event.stopPropagation()">
          <mat-icon>notifications_off</mat-icon>
          <span>{{ 'DMS.NOTIF.EMPTY' | translate }}</span>
        </div>
      </ng-template>
    </mat-menu>
  `
})
export class AppNotificationsComponent implements OnInit {
  readonly notifications = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly POLL_MS = 60_000;
  readonly groups: AppNotificationKind[] = ['task', 'pool', 'expiry'];

  ngOnInit(): void {
    this.notifications.refresh();
    interval(AppNotificationsComponent.POLL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.notifications.refresh());
  }

  /** Another look on every open — cheaper than trusting the last poll. */
  opened(): void {
    this.notifications.refresh();
  }

  byKind(kind: AppNotificationKind): AppNotification[] {
    return this.notifications.items().filter((item) => item.kind === kind);
  }

  iconFor(kind: AppNotificationKind): string {
    return kind === 'task' ? 'assignment' : kind === 'pool' ? 'group_add' : 'event_busy';
  }

  open(item: AppNotification): void {
    this.notifications.markSeen(item.id);
    void this.router.navigate(item.route);
  }
}
